import React, { useState, useEffect } from 'react';
import styles from './index.module.scss';
import jwtDecode from 'jwt-decode';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSearch } from '@fortawesome/free-solid-svg-icons';
import { INVALID_REPAIR_ORDER_SEARCH } from '../../constants/status';

// Components
import { Button, Spinner } from 'react-bootstrap';
import RepairList from './RepairList';
import CreateRepairModal from './CreateRepairModal';
import ReceiptModal from '../../components/ReceiptModal';
import CustomPagination from '../../components/CustomPagination';
import SearchSection from './SearchSection';
import AuthModal from '../../components/AuthModal';

const RepairScreen = (props) => {
    const [newOrderCreated, setNewOrderCreated] = useState(null);
    const [showReceipt, setShowReceipt] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [showAuth, setShowAuth] = useState(false);
    const [authOptions, setAuthOptions] = useState(null);
    const [searchHistory, setSearchHistory] = useState();
    const [repairsList, setRepairList] = useState([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [repairSearchError, setRepairSearchError] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const loadRepairs = async () => {
            setLoading(true);
            const url = process.env.REACT_APP_API_URL;
            const authToken = sessionStorage.getItem('auth-token');
            let searchUrl;

            // Check if there is a history
            if (searchHistory) {
                searchUrl = `${searchHistory}&page=${page}`;
            } else {
                searchUrl = `${url}/repairs?limit=10&page=${page}`;
            }

            const request = await fetch(searchUrl,
                {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                    }
                });

            if (request.status === 200) {
                const fetchRepairs = await request.json();
                setRepairList(fetchRepairs.list);
                setTotalPages(fetchRepairs.total);
            }

            setLoading(false);
        };


        loadRepairs();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, newOrderCreated]);

    const showReceiptHandler = (order) => {
        setNewOrderCreated(order);
        setShowReceipt(true);
    }

    const searchRepair = async (search, filter) => {
        // Reset Repair Search Error
        if (repairSearchError)
            setRepairSearchError(false);

        setLoading(true);
        const url = process.env.REACT_APP_API_URL;
        const authToken = sessionStorage.getItem('auth-token');
        const searchUrl = `${url}/repairs?limit=10&s=${search}&f=${filter}`;
        setPage(1);
        setSearchHistory(searchUrl);

        const request = await fetch(`${searchUrl}&page=1`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                }
            });

        if (request.status === 200) {
            const fetchRepairs = await request.json();
            setRepairList(fetchRepairs.list);
            setTotalPages(fetchRepairs.total);
        } else {
            const response = await request.json();
            switch (response.status) {
                case INVALID_REPAIR_ORDER_SEARCH:
                    setRepairSearchError(true);
                    break;
                default:

            }
        }

        setLoading(false);
    }

    const createRepair = async ({state}) => {
        setLoading(true); // Start the loading Spinner
        const url = process.env.REACT_APP_API_URL;
        const authToken = sessionStorage.getItem('auth-token');
        const userId = jwtDecode(authToken).id;

        // Data to be sent
        const postData = {
            service: state.service.value,
            client: {
                name: state.customer.value,
                phone: state.phone.value,
            },
            device: {
                name: state.device.value,
                password: state.password.value
            },
            total: +state.total.value,
            paid: +state.deposit.value,
            notes: state.notes.value,
            userId: userId
        };

        const request = await fetch(`${url}/repairs`,
            {
                method: 'POST',
                body: JSON.stringify(postData),
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                }
            });

        setLoading(false); // Remove the loading Spinner
        if (request.status === 201) {
            const order = await request.json();
            showReceiptHandler(order);

            return true;
        } else {
            const response = await request.json();

            // switch (response.status) {
            //     case REPAIR_CREATION_FAILED:
            //         setErrorMessage("Hubo un error al crear la reparación. (Codigo de Error #00-1)");
            //         break;
            //     case REPAIR_VALIDATION_ERROR:
            //         setErrorMessage("Alguna casillas no son aceptada por el servidor. (Codigo de Error #00-3)")
            //         break;
            //     default:
            //         setErrorMessage("Hubo un error. (Codigo de Error #00-2)");
            // }
        }

        return false;
    }

    const startAuthentication = (action, options = {}, cb = () => null) => {
        setShowAuth(true);
        setAuthOptions({
            type: action,
            ...options,
        });

        cb();
    }

    const submitAuthentication = async () => {
        switch(authOptions.type) {
            case "CREATE_ORDER":
                await createRepair(authOptions);
                break;
            default:
                console.debug("Unknown Auth Type");
        }
    };

    return (
        <section className={styles.RepairScreen}>
            <div className={styles.Heading}>
                <h4 className={styles.Title}>Reparaciones</h4>
                <div className={styles.Actions}>
                    <Button variant="secondary" className={styles.ActionButton} onClick={() => setShowSearch(search => !search)}>
                        <FontAwesomeIcon className={styles.Icon} icon={faSearch} />
                        <span>Buscar</span>
                    </Button>
                    <Button variant="success" className={styles.ActionButton} onClick={() => setShowAddModal(true)}>
                        <FontAwesomeIcon className={styles.Icon} icon={faPlus} />
                        <span>Nueva Reparación</span>
                    </Button>
                </div>
            </div>
            {showSearch && <SearchSection
                onSearch={searchRepair}
                isInvalid={repairSearchError} />}
            {!loading ? <RepairList repairs={repairsList} /> : <Spinner className={styles.LoadingSpinner} animation="border" />}
            {!loading && <CustomPagination
                total={totalPages}
                current={page}
                limit={5}
                setPage={setPage} />}
            <CreateRepairModal
                key="RepairCreateModal"
                show={showAddModal && !showAuth}
                onHide={() => setShowAddModal(false)}
                createRepair={startAuthentication} />
            <ReceiptModal
                show={showReceipt}
                onClose={() => setShowReceipt(false)}
                order={newOrderCreated} />
            <AuthModal
                show={showAuth}
                hide={() => setShowAuth(false)}
                userData={props.userData}
                func={submitAuthentication} />
        </section>
    )
};

export default RepairScreen;